/**
 * Quality Presets - Adaptive graphics settings for different hardware tiers
 *
 * Provides Ultra / High / Medium / Low presets that control:
 * - Post-processing (SSAO, bloom, god rays)
 * - Shadow quality (map size, distance, shadow-casting lights)
 * - Visibility radius (draw distance in chunks)
 * - Texture anisotropy
 * - Water quality
 *
 * Colour correction is always on (not a preset lever). MSAA, Resolution and FoV are
 * standalone user settings (see the store), not part of any preset. Terrain detail maps
 * (normal/AO/metalness) ramp with the preset — Off on low, Normal on medium, Full (all
 * three) on high/ultra — surfaced as the "Detail Maps" segmented row.
 *
 * `QUALITY_ROWS` (below) is the declarative source for the segmented Quality UI: each row
 * is one labelled control whose segments carry the exact `QualitySettings` patch they
 * apply. The preset table and the UI both read from it, so there is one place to edit.
 */

import { VISIBILITY_RADIUS } from '@worldify/shared';
import {
  MAX_BASE_LOD,
  MAX_FAR_LOD_LEVELS,
  MAX_PACKABLE_LOD,
} from '../voxel/LodShellSelector.js';

export type QualityLevel = 'ultra' | 'high' | 'medium' | 'low' | 'verylow';

export const QUALITY_LEVELS: QualityLevel[] = ['verylow', 'low', 'medium', 'high', 'ultra'];

export const QUALITY_LABELS: Record<QualityLevel, string> = {
  ultra: 'Ultra',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  verylow: 'Very Low',
};

/**
 * THE LOW TIER — the presets that render for a weak GPU rather than for looks. Very Low is the floor;
 * Low is Very Low plus Medium's shadows and nothing else. What the tier still decides: foliage casts
 * as solid cards (setShadowCasterDetail) and the shadow map redraws half as often (Lighting). The
 * render path itself is the same on every tier (render/PostPipeline.ts) — a preset skips stages.
 */
export function isLowTier(level: QualityLevel): boolean {
  return level === 'verylow' || level === 'low';
}

export interface QualitySettings {
  // Post-processing (individual toggles)
  ssaoEnabled: boolean;
  bloomEnabled: boolean;
  godRaysEnabled: boolean;    // radial-blur rays from the sun/moon — high + ultra only
  godRaysSamples: number;     // radial-blur sample count (20 = Low, 60 = High)

  // Shadows
  shadowMapSize: number;      // 512, 1024, 2048, 4096 (applied via environment settings)
  shadowsEnabled: boolean;    // false = "Off"; the moon casts too whenever this is on
  shadowRadius: number;       // Shadow casting distance in chunks (independent of visibility)

  // Terrain
  /** Chunk draw distance. NOT a per-preset knob any more — every preset starts at
   *  START_VIEW_DISTANCE and the player raises it from there; this is the fallback for a caller that
   *  omits the current radius, and it must agree with the boot value or a preset switch would
   *  silently move the view. */
  visibilityRadius: number;
  farLodLevels: number;       // Coarser far-view LOD rings around the base region (0 = off)
  anisotropy: number;         // Texture anisotropic filtering (1/2/4/8/16)

  // Shader map toggles — on for every preset; debug panel may flip them for shader work
  shaderNormalMaps: boolean;
  shaderAoMaps: boolean;
  shaderMetalnessMaps: boolean;

  // Water: full 4-layer normals + second normal sample (ultra/high) vs cheap 2-layer (medium/low)
  waterHighQuality: boolean;
}

/**
 * Selectable view distances, in chunks — the base-LOD cube's half-extent. THE one list: the Quality
 * row, the explore Settings pills, and snapViewDistance all read it, and VISIBILITY_RADIUS (the BFS
 * scratch-grid size) must be ≥ the largest of them.
 */
export const VIEW_DISTANCES = [1, 3, 5, 7, 9] as const;
/** Largest selectable view distance — what the BFS grid has to be sized for. */
export const MAX_VIEW_DISTANCE = VIEW_DISTANCES[VIEW_DISTANCES.length - 1];
// The BFS clips traversal at its grid edge, so an option beyond VISIBILITY_RADIUS would simply stop
// loading past that ring with nothing to show why. Same seam as the Far View assertion below.
if (MAX_VIEW_DISTANCE > VISIBILITY_RADIUS) {
  throw new Error(
    `View Distance offers ${MAX_VIEW_DISTANCE} but the visibility BFS grid is sized for `
    + `${VISIBILITY_RADIUS} — raise VISIBILITY_RADIUS in shared/src/voxel/constants.ts.`);
}
/**
 * THE VIEW DISTANCE EVERY SESSION STARTS AT — the nearest option, on every device, whatever the
 * quality auto-detect says and whatever was chosen last session.
 *
 * Pinned to a literal rather than VIEW_DISTANCES[0] — the list gained a nearer option (1) for
 * testing, and the boot view must not follow it.
 *
 * View distance is the one setting that decides how long a cold start takes: the base region is a
 * cube, so each step out is ~2.4x the chunks to generate, mesh and merge before anything is drawable.
 * Starting near means the world around the player fills fast and the rings refine outward from
 * there; raising it is one tap away, and the fine-first ladder then fills the extra shells in order.
 * A strong PC is not a reason to start slow, which is why this is not read from the preset.
 */
export const START_VIEW_DISTANCE: (typeof VIEW_DISTANCES)[number] = 3;

/**
 * Far View options: `levels` IS the number of coarse rings streamed around the base region, and the
 * label says so — 2 rings, 4 rings, 6 rings, 8 rings. (It used to label the view-extent MULTIPLIER
 * 2×/4×/8× over levels 1/2/3, which meant the number on screen and the number in the code were
 * different things.) One source for both the Quality row and the explore panel's View control.
 *
 * Each ring doubles the visible extent at 1/8 the voxel density of the level below, and costs a
 * roughly CONSTANT number of chunks (measured: ~216 per ring at view distance 3, ~384 at 5 — the shell
 * shrinks as fast as it dilates), which is what makes a deep far view affordable at all.
 *
 * The packing envelope allows up to MAX_FAR_LOD_LEVELS (12); 8 is where the cost stops buying
 * visible world (~5 km at view distance 3).
 */
export const FAR_VIEW_OPTIONS = [
  { label: '0', levels: 0 },
  { label: '2', levels: 2 },
  { label: '4', levels: 4 },
  { label: '6', levels: 6 },
  { label: '8', levels: 8 },
] as const;
/**
 * Distance presets — the explore panel's one "Distance" control, pairing the lod-0 view distance
 * with the coarse ring count so the player picks a single named reach instead of two numbers.
 *
 * Only `tiny` moves the lod-0 radius: below 3 the base region is small enough that the rings do
 * the work, and above it a cold start pays ~2.4× per step (see START_VIEW_DISTANCE). Everything
 * from `short` up buys its distance with rings, which cost a roughly constant number of chunks
 * each. The debug panel still exposes the two axes separately.
 */
export const DISTANCE_PRESETS = [
  { label: 'Tiny', view: 1, far: 0 },
  { label: 'Short', view: 3, far: 0 },
  { label: 'Close', view: 3, far: 2 },
  { label: 'Normal', view: 3, far: 4 },
  { label: 'Far', view: 3, far: 6 },
  { label: 'Extreme', view: 3, far: 8 },
] as const;

/** Deepest ring count the UI offers. Must fit the engine's packing envelope — asserted below. */
export const MAX_FAR_VIEW_LEVELS = FAR_VIEW_OPTIONS[FAR_VIEW_OPTIONS.length - 1].levels;
/** Touch cap: mobile keeps the smallest non-zero far view by default. */
export const TOUCH_MAX_FAR_LOD_LEVELS = 2;

// The UI's deepest option and the engine's packing ceiling are set in different files for good
// reasons (one is product, one is a bit-budget). This is the seam that used to drift silently, so it
// fails at module load instead: maxLod = MAX_BASE_LOD + farLodLevels must stay inside packColumn.
if (MAX_FAR_VIEW_LEVELS > MAX_FAR_LOD_LEVELS) {
  throw new Error(
    `Far View offers ${MAX_FAR_VIEW_LEVELS} rings but the LOD packing supports `
    + `${MAX_FAR_LOD_LEVELS} (MAX_PACKABLE_LOD=${MAX_PACKABLE_LOD}, MAX_BASE_LOD=${MAX_BASE_LOD}) — `
    + `widen LOD_PACK_STRIDE in shared packedKey.ts before adding the option.`);
}

/** Snap a persisted/arbitrary ring count to the nearest offered option (ties snap low). */
export function snapFarLodLevels(v: number): number {
  return FAR_VIEW_OPTIONS.reduce(
    (best, o) => (Math.abs(o.levels - v) < Math.abs(best - v) ? o.levels : best),
    FAR_VIEW_OPTIONS[0].levels as number,
  );
}

/**
 * Snap an arbitrary view distance to the nearest selectable value. Used to migrate a persisted radius
 * from an older build (e.g. 2/4/6/8) into the current set, so the slider highlights and the value
 * never exceeds VISIBILITY_RADIUS (the BFS grid size). Ties snap to the lower option.
 */
export function snapViewDistance(v: number): number {
  return VIEW_DISTANCES.reduce(
    (best, d) => (Math.abs(d - v) < Math.abs(best - v) ? d : best),
    VIEW_DISTANCES[0] as number,
  );
}

/** A preset decides LOOKS only. View distance and far rings are the player's, never a preset's. */
export type QualityPreset = Omit<QualitySettings, 'visibilityRadius' | 'farLodLevels'>;

/** Far rings on a fresh install (the "Normal" distance preset); a saved choice overrides it. */
export const DEFAULT_FAR_LOD_LEVELS = 4;

export const QUALITY_PRESETS: Record<QualityLevel, QualityPreset> = {
  ultra: {
    ssaoEnabled: true,
    bloomEnabled: true,
    godRaysEnabled: true,
    godRaysSamples: 60,   // High
    shadowMapSize: 4096,
    shadowsEnabled: true,
    shadowRadius: 8,
    anisotropy: 16,
    shaderNormalMaps: true,
    shaderAoMaps: true,
    shaderMetalnessMaps: true,
    waterHighQuality: true,
  },
  high: {
    ssaoEnabled: true,
    bloomEnabled: true,
    godRaysEnabled: true,
    godRaysSamples: 20,   // Low
    shadowMapSize: 2048,
    shadowsEnabled: true,
    shadowRadius: 6,
    anisotropy: 8,
    shaderNormalMaps: true,
    shaderAoMaps: true,
    shaderMetalnessMaps: true,
    waterHighQuality: true,
  },
  medium: {
    ssaoEnabled: false,
    bloomEnabled: false,
    godRaysEnabled: false,
    godRaysSamples: 20,   // unused (god rays off)
    shadowMapSize: 1024,
    shadowsEnabled: true,
    shadowRadius: 4,
    anisotropy: 2,
    shaderNormalMaps: true,    // Normal maps on from medium
    shaderAoMaps: false,
    shaderMetalnessMaps: false,
    waterHighQuality: false,
  },
  // Low = Very Low + shadows (a 512 map over Medium's 4-chunk radius — the same softness as 1024
  // since the blur scales with the map, a quarter of the caster fill), nothing else — see isLowTier.
  low: {
    ssaoEnabled: false,
    bloomEnabled: false,
    godRaysEnabled: false,
    godRaysSamples: 20,   // unused (god rays off)
    shadowMapSize: 512,
    shadowsEnabled: true,
    shadowRadius: 4,
    anisotropy: 1,
    shaderNormalMaps: false,   // no detail maps on the low tier
    shaderAoMaps: false,
    shaderMetalnessMaps: false,
    waterHighQuality: false,
  },
  verylow: {
    ssaoEnabled: false,
    bloomEnabled: false,
    godRaysEnabled: false,
    godRaysSamples: 20,   // unused (god rays off)
    shadowMapSize: 512,
    shadowsEnabled: false,
    shadowRadius: 2,
    anisotropy: 1,
    shaderNormalMaps: false,   // no detail maps on the low tier
    shaderAoMaps: false,
    shaderMetalnessMaps: false,
    waterHighQuality: false,
  },
};

// ============== Segmented Quality UI descriptor ==============

/**
 * One segment of a Quality row — a label plus the exact `QualitySettings` patch it applies
 * when selected. `applyQualityPatch` (QualityManager) routes the patch to the right
 * subsystems, so the segment table below is the single source for both the preset values
 * and the in-game control.
 */
export interface QualitySegment {
  label: string;
  patch: Partial<QualitySettings>;
}

export interface QualityRow {
  key: string;
  label: string;
  segments: QualitySegment[];
  /** Index of the segment matching the given settings, or -1 if none match. */
  match: (q: QualitySettings) => number;
}

const shadowMapIndex: Record<number, number> = { 512: 1, 1024: 2, 2048: 3, 4096: 4 };

export const QUALITY_ROWS: QualityRow[] = [
  {
    key: 'shadows',
    label: 'Shadows',
    segments: [
      { label: 'Off', patch: { shadowsEnabled: false } },
      { label: '512', patch: { shadowsEnabled: true, shadowMapSize: 512 } },
      { label: '1024', patch: { shadowsEnabled: true, shadowMapSize: 1024 } },
      { label: '2048', patch: { shadowsEnabled: true, shadowMapSize: 2048 } },
      { label: '4096', patch: { shadowsEnabled: true, shadowMapSize: 4096 } },
    ],
    match: (q) => (!q.shadowsEnabled ? 0 : shadowMapIndex[q.shadowMapSize] ?? -1),
  },
  {
    key: 'shadowDistance',
    label: 'Shadow Distance',
    segments: [2, 4, 6, 8].map((n) => ({ label: String(n), patch: { shadowRadius: n } })),
    match: (q) => [2, 4, 6, 8].indexOf(q.shadowRadius),
  },
  {
    key: 'ssao',
    label: 'Ambient Occlusion',
    segments: [
      { label: 'Off', patch: { ssaoEnabled: false } },
      { label: 'On', patch: { ssaoEnabled: true } },
    ],
    match: (q) => (q.ssaoEnabled ? 1 : 0),
  },
  {
    key: 'bloom',
    label: 'Bloom',
    segments: [
      { label: 'Off', patch: { bloomEnabled: false } },
      { label: 'On', patch: { bloomEnabled: true } },
    ],
    match: (q) => (q.bloomEnabled ? 1 : 0),
  },
  {
    key: 'godRays',
    label: 'God Rays',
    segments: [
      { label: 'Off', patch: { godRaysEnabled: false } },
      { label: '20', patch: { godRaysEnabled: true, godRaysSamples: 20 } },
      { label: '60', patch: { godRaysEnabled: true, godRaysSamples: 60 } },
    ],
    match: (q) => (!q.godRaysEnabled ? 0 : q.godRaysSamples >= 60 ? 2 : 1),
  },
  {
    key: 'viewDistance',
    label: 'View Distance',
    segments: VIEW_DISTANCES.map((n) => ({ label: String(n), patch: { visibilityRadius: n } })),
    match: (q) => VIEW_DISTANCES.indexOf(q.visibilityRadius as (typeof VIEW_DISTANCES)[number]),
  },
  {
    // Far-view LOD rings: the label IS the ring count (see FAR_VIEW_OPTIONS). Each ring doubles the
    // visible world extent at 1/8 the voxel density of the level below.
    key: 'farView',
    label: 'Far View',
    segments: FAR_VIEW_OPTIONS.map((o) => ({ label: o.label, patch: { farLodLevels: o.levels } })),
    match: (q) => FAR_VIEW_OPTIONS.findIndex((o) => o.levels === q.farLodLevels),
  },
  {
    key: 'anisotropy',
    label: 'Texture Filtering',
    segments: [1, 2, 4, 8, 16].map((n) => ({ label: String(n), patch: { anisotropy: n } })),
    match: (q) => [1, 2, 4, 8, 16].indexOf(q.anisotropy),
  },
  {
    key: 'water',
    label: 'Water',
    segments: [
      { label: 'Low', patch: { waterHighQuality: false } },
      { label: 'High', patch: { waterHighQuality: true } },
    ],
    match: (q) => (q.waterHighQuality ? 1 : 0),
  },
  {
    // Terrain detail maps ramp: Off (low) → Normal only (medium) → Normal+AO+Metalness (high+).
    key: 'detailMaps',
    label: 'Detail Maps',
    segments: [
      { label: 'Off', patch: { shaderNormalMaps: false, shaderAoMaps: false, shaderMetalnessMaps: false } },
      { label: 'Normal', patch: { shaderNormalMaps: true, shaderAoMaps: false, shaderMetalnessMaps: false } },
      { label: 'Full', patch: { shaderNormalMaps: true, shaderAoMaps: true, shaderMetalnessMaps: true } },
    ],
    match: (q) => (!q.shaderNormalMaps ? 0 : (q.shaderAoMaps && q.shaderMetalnessMaps ? 2 : 1)),
  },
];

/**
 * Whether the live quality settings still match a preset exactly. View distance and far rings are
 * not preset fields, so changing them never reads as "Custom". Used by the UI to highlight the active
 * preset button or show a "Custom" badge.
 */
export function qualityMatchesPreset(q: QualitySettings, level: QualityLevel): boolean {
  const p = QUALITY_PRESETS[level];
  const keys = Object.keys(p) as (keyof QualityPreset)[];
  return keys.every((k) => q[k] === p[k]);
}

/** MSAA is a standalone user setting (not preset-driven); shown as a Quality row for grouping. */
export const MSAA_OPTIONS: { label: string; value: number }[] = [
  { label: 'Off', value: 0 },
  { label: '2×', value: 2 },
  { label: '4×', value: 4 },
];

/** Next preset when cycling with F8 */
export function cycleQualityLevel(current: QualityLevel): QualityLevel {
  const idx = QUALITY_LEVELS.indexOf(current);
  return QUALITY_LEVELS[(idx + 1) % QUALITY_LEVELS.length];
}

/** localStorage key for persisted quality setting */
const QUALITY_STORAGE_KEY = 'worldify-quality-level';
/** Set once the Low → Very Low rename has been applied to (or is moot for) this browser's save. */
const QUALITY_MIGRATED_KEY = 'worldify-quality-level-v2';
const FAR_LODS_STORAGE_KEY = 'worldify-far-lods';

/** Save quality level to localStorage */
export function saveQualityLevel(level: QualityLevel): void {
  try {
    localStorage.setItem(QUALITY_STORAGE_KEY, level);
  } catch { /* ignore */ }
}

/** Load quality level from localStorage */
export function loadSavedQualityLevel(): QualityLevel | null {
  try {
    const saved = localStorage.getItem(QUALITY_STORAGE_KEY);
    if (saved && QUALITY_LEVELS.includes(saved as QualityLevel)) {
      // 'low' was renamed Very Low when a shadows-on Low was added above it; a session saved before
      // that keeps the preset it actually had (no shadows) rather than silently gaining a shadow map.
      // Written back once so the migration is a one-shot: choosing the new Low later persists as 'low'.
      if (saved === 'low' && localStorage.getItem(QUALITY_MIGRATED_KEY) === null) {
        localStorage.setItem(QUALITY_MIGRATED_KEY, '1');
        localStorage.setItem(QUALITY_STORAGE_KEY, 'verylow');
        return 'verylow';
      }
      localStorage.setItem(QUALITY_MIGRATED_KEY, '1');
      return saved as QualityLevel;
    }
  } catch { /* ignore */ }
  return null;
}

/** Save custom far-LOD levels override to localStorage */
export function saveFarLodLevels(levels: number): void {
  try {
    localStorage.setItem(FAR_LODS_STORAGE_KEY, String(levels));
  } catch { /* ignore */ }
}

/** Load custom far-LOD levels override from localStorage */
export function loadSavedFarLodLevels(): number | null {
  try {
    const saved = localStorage.getItem(FAR_LODS_STORAGE_KEY);
    if (saved) {
      const val = parseInt(saved, 10);
      // Snapped, not just range-checked: a value saved before Far View meant RING COUNT (0-3) is a
      // legal count now, so it would silently persist as an off-list setting no pill matches.
      if (!isNaN(val) && val >= 0 && val <= MAX_FAR_VIEW_LEVELS) return snapFarLodLevels(val);
    }
  } catch { /* ignore */ }
  return null;
}

/**
 * Auto-detect quality level from GPU capabilities.
 * Runs a lightweight heuristic: checks renderer string, max texture size,
 * device pixel ratio, and available memory hints.
 */
export function detectQualityLevel(renderer: WebGLRenderingContext | WebGL2RenderingContext): QualityLevel {
  const debugExt = renderer.getExtension('WEBGL_debug_renderer_info');
  const gpuRenderer = debugExt
    ? renderer.getParameter(debugExt.UNMASKED_RENDERER_WEBGL) as string
    : '';
  const maxTextureSize = renderer.getParameter(renderer.MAX_TEXTURE_SIZE) as number;

  console.log(`[Quality] GPU: ${gpuRenderer}, maxTex: ${maxTextureSize}, dpr: ${window.devicePixelRatio}`);

  // Heuristic scoring
  let score = 0;

  // GPU string heuristics
  const gpu = gpuRenderer.toLowerCase();
  if (/rtx\s*(40[5-9]0|4080|4090|50[5-9]0|5080|5090)/i.test(gpuRenderer)) {
    score += 4; // High-end desktop
  } else if (/rtx\s*(30[5-9]0|3080|3090)/i.test(gpuRenderer)) {
    score += 3;
  } else if (/rtx|radeon\s*rx\s*(6[7-9]|7[0-9])/i.test(gpuRenderer)) {
    score += 2;
  } else if (/gtx\s*1[0-9]{3}|radeon\s*rx\s*(5[5-9]|6[0-6])/i.test(gpuRenderer)) {
    score += 1;
  } else if (gpu.includes('intel') || gpu.includes('iris') || gpu.includes('uhd')) {
    score -= 1; // Integrated
  } else if (gpu.includes('apple') && gpu.includes('m')) {
    // Apple Silicon — M1/M2/M3 are decent
    score += 2;
  } else if (gpu.includes('mali') || gpu.includes('adreno') || gpu.includes('powervr')) {
    score -= 2; // Mobile
  }

  // Texture size as a proxy for GPU tier
  if (maxTextureSize >= 16384) score += 1;
  else if (maxTextureSize <= 4096) score -= 1;

  // High DPI display suggests laptop (more pixels to push)
  if (window.devicePixelRatio > 1.5) score -= 1;

  // Determine level from score
  if (score >= 3) return 'ultra';
  if (score >= 1) return 'high';
  if (score >= 0) return 'medium';
  return 'verylow';
}
